import * as React from 'react';
import {makeStyles} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {
  ActiveEligibilityStatusKeys,
  AppPermissions,
  USER,
  VerificationStatusObj
} from '@shared/constants/Mapping';
import {getSpecificPatientDetails} from '@shared/services/PatientsServices';
import {verifyEligibility} from '@shared/services/InsuranceEligibilityServices';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {appointmentsDateTimeFormat} from '@shared/utils/DateTimeUtils';
import {useSelector} from 'react-redux';
import {APP_PRIMARY_COLOR} from 'src/style/variable';
import VerifyInsuranceModal from './VerifyInsuranceModal';
import DropDownFieldValue from '@components/system/DropDownFieldValue/DropDownFieldValue';
import RefreshIcon from '@material-ui/icons/Refresh';
import {CommunicationService} from 'src/service/CommunicationService';

import {State} from '@shared/store/reducers/Index';
import {Permission} from '@shared/services/PermissionService';
import {Insurance} from './EligibilityVerificationMapper';
const styles = makeStyles(() => ({
  formControl: {
    width: '100%',
    marginBottom: 0
  },
  linkStyle: {
    lineHeight: '1.25rem',
    cursor: 'pointer',
    color: APP_PRIMARY_COLOR,
    textDecoration: 'underline'
  },
  refreshingAndVerifying: {
    display: 'flex',
    gap: '0.3rem',
    alignItems: 'center'
  }
}));

interface VerifyInsuranceProps {
  clinicGroupId: string | number;
  clinicId: string | number;
  patientId: string | number;
  appointment: any;
  hasCoverages?: boolean;
  inAppointmentCard?: boolean;
  etaDate: string | undefined;
  isEligibilityVerificationEnabled?: boolean;
  onRefreshInsurance?: () => void;
  verifyingInsurance?: any;
}

function insuranceOnPriorityComp(insurance1: any, insurance2: any) {
  const insuranceDefaultPriority = 100;
  const insurancePriorityMapper = {
    primary: 0,
    secondary: 1,
    tertiary: 2
  };
  let insurance1PriorityVal = insuranceDefaultPriority;
  if (insurance1.priority in insurancePriorityMapper) {
    insurance1PriorityVal =
      insurancePriorityMapper[
        insurance1.priority as keyof typeof insurancePriorityMapper
      ];
  }

  let insurance2PriorityVal = insuranceDefaultPriority;
  if (insurance2.priority in insurancePriorityMapper) {
    insurance2PriorityVal =
      insurancePriorityMapper[
        insurance2.priority as keyof typeof insurancePriorityMapper
      ];
  }

  return insurance1PriorityVal - insurance2PriorityVal;
}

const isActiveInsurance = (insuranceValue: string) => {
  return ActiveEligibilityStatusKeys.includes(insuranceValue);
};

const VerifyInsurance = ({
  clinicGroupId,
  clinicId,
  patientId,
  appointment,
  etaDate,
  hasCoverages = false,
  inAppointmentCard = true,
  isEligibilityVerificationEnabled = false,
  verifyingInsurance,
  onRefreshInsurance
}: VerifyInsuranceProps): JSX.Element => {
  const [
    verifyInsuranceModalState,
    setVerifyInsuranceModalState
  ] = React.useState({
    isOpen: false,
    insurance: null as any
  });

  const [isVerifying, setIsVerifying] = React.useState(false);
  const [isRefreshingInsurance, setIsRefreshingInsurance] = React.useState(
    false
  );
  const classes = styles();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const zone = useSelector((state: any) => state.clinics.zone);

  const handleVerifyInsuranceModalClose = () => {
    setVerifyInsuranceModalState({
      isOpen: false,
      insurance: null
    });
  };
  const handleVerifyInsuranceModalOpen = (insurance = null) => {
    setVerifyInsuranceModalState({
      isOpen: true,
      insurance
    });
  };

  const {userType, userPermissions} = useSelector((state: State) => state.auth);

  const isUserInsuranceRolePermissionExists =
    userType === USER.ADMIN ||
    Permission.hasEligibilityVerificationPermissions(
      userPermissions,
      AppPermissions.ELIGIBILITY_VERIFICATION?.EDIT,
      clinicGroupId
    );

  React.useEffect(() => {
    // Reset state
    setIsVerifying(false);
    if (verifyingInsurance && typeof verifyingInsurance === 'function') {
      verifyingInsurance(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment]);

  const handleRefreshInsuranceClick = async () => {
    try {
      setIsRefreshingInsurance(true);

      // Get patient details
      const res = await getSpecificPatientDetails(
        clinicGroupId as number,
        clinicId as number,
        patientId as number
      );
      let hasActiveInsurance = false;
      if (
        res?.data?.insurance &&
        Array.isArray(res?.data?.insurance) &&
        res?.data?.insurance.length > 0
      ) {
        hasActiveInsurance = res.data.insurance.some(
          (data: Insurance) =>
            data.payer_name &&
            data.subscriber_member_no &&
            (data.subscriber_group_no || data.rx_group_no) &&
            data.is_valid_insurance
        );
      }
      if (hasActiveInsurance) {
        const payload = {
          patient_id: patientId,
          is_refresh: true
        };
        // Call eligibility verification api
        const adhocRes = await verifyEligibility(
          clinicGroupId,
          clinicId,
          appointment?.id,
          payload
        );
        enqueueSnackbar(adhocRes?.message, SnackBarConfig('s'));
        if (onRefreshInsurance && typeof onRefreshInsurance === 'function') {
          onRefreshInsurance();
        }
      } else {
        handleVerifyInsuranceModalOpen();
      }
    } catch (error: any) {
      enqueueSnackbar(
        error?.message
          ? error.message
          : t(`ELIGIBILITY_VERIFICATION.ERROR_IN_REFRESHING_INSURANCE_DETAILS`),
        SnackBarConfig('e')
      );
    } finally {
      setIsRefreshingInsurance(false);
    }
  };

  const dispatchRefreshAppointmentsEvent = () => {
    CommunicationService.sendEvent({
      action: 'REFRESH_APPOINTMENT',
      data: {
        clinicGroupId,
        clinicId,
        appointmentId: appointment?.id
      }
    });
  };

  const handleVerifyInsuranceClick = async () => {
    try {
      if (verifyingInsurance && typeof verifyingInsurance === 'function') {
        verifyingInsurance(true);
      }
      setIsVerifying(true);
      // Get patient details
      const res = await getSpecificPatientDetails(
        clinicGroupId as number,
        clinicId as number,
        patientId as number
      );
      let insurance = null;
      if (
        res?.data?.insurance &&
        Array.isArray(res?.data?.insurance) &&
        res?.data?.insurance.length > 0
      ) {
        const sortedInsurance = [...res.data.insurance].sort(
          insuranceOnPriorityComp
        );
        insurance = sortedInsurance.find(
          (data: Insurance) =>
            (data.payer_name ||
              data.subscriber_member_no ||
              data.subscriber_group_no) &&
            data.is_valid_insurance !== false
        );
      }
      handleVerifyInsuranceModalOpen(insurance);
    } catch (error: any) {
      enqueueSnackbar(
        error?.message
          ? error.message
          : t('ELIGIBILITY_VERIFICATION.ERROR_IN_VERIFYING_INSURANCE_DETAILS'),
        SnackBarConfig('e')
      );
      setIsVerifying(false);
      if (verifyingInsurance && typeof verifyingInsurance === 'function') {
        verifyingInsurance(false);
      }
    }
  };

  const renderVerifyInsuranceLink = () => {
    return (
      <p
        className={!isVerifying ? classes.linkStyle : ''}
        onClick={async () => {
          if (!isVerifying) {
            await handleVerifyInsuranceClick();
          }
        }}>
        {t('ELIGIBILITY_VERIFICATION.VERIFY_INSURANCE_NOW')}
      </p>
    );
  };

  const renderRefreshInsuranceDetailsLink = () => {
    return (
      <p
        className={`${!isVerifying ? classes.linkStyle : ''}`}
        onClick={async () => {
          if (!isVerifying) {
            await handleRefreshInsuranceClick();
          }
        }}>
        {t('ELIGIBILITY_VERIFICATION.REFRESH_INSURANCE_DETAILS_NOW')}
      </p>
    );
  };

  const renderVerificationProcessing = () => {
    return inAppointmentCard ? (
      <DropDownFieldValue
        label={
          <div className={classes.refreshingAndVerifying}>
            <p>{t('ELIGIBILITY_VERIFICATION.VERIFICATION_PROCESSING')}</p>
            <RefreshIcon
              style={{color: APP_PRIMARY_COLOR, cursor: 'pointer'}}
              fontSize={'small'}
              onClick={dispatchRefreshAppointmentsEvent}
            />
          </div>
        }
        value={
          appointment?.['coverage_eta_date'] || etaDate
            ? `${t(
                'ELIGIBILITY_VERIFICATION.ETA'
              )}:${' '}${appointmentsDateTimeFormat(
                appointment?.['coverage_eta_date'] || etaDate,
                zone
              )}`
            : ''
        }
      />
    ) : (
      <>
        <div className={classes.refreshingAndVerifying}>
          <p>{t('ELIGIBILITY_VERIFICATION.VERIFICATION_PROCESSING')}</p>
          <RefreshIcon
            fontSize={'small'}
            style={{color: APP_PRIMARY_COLOR, cursor: 'pointer'}}
            onClick={dispatchRefreshAppointmentsEvent}
          />
        </div>
        {(appointment?.['coverage_eta_date'] || etaDate) && (
          <p>
            {t('ELIGIBILITY_VERIFICATION.ETA')}:{' '}
            {appointmentsDateTimeFormat(
              appointment?.['coverage_eta_date'] || etaDate,
              zone
            )}
          </p>
        )}
      </>
    );
  };

  const renderRefreshingInsuranceDetails = () => {
    return inAppointmentCard ? (
      <DropDownFieldValue
        label={t('ELIGIBILITY_VERIFICATION.REFRESHING_INSURANCE_DETAILS')}
        value={
          appointment?.['coverage_eta_date'] || etaDate
            ? `${t(
                'ELIGIBILITY_VERIFICATION.ETA'
              )}:${' '}${appointmentsDateTimeFormat(
                appointment?.['coverage_eta_date'] || etaDate,
                zone
              )}`
            : ''
        }
      />
    ) : (
      <>
        <p>{t('ELIGIBILITY_VERIFICATION.REFRESHING_INSURANCE_DETAILS')}</p>
        {(appointment?.['coverage_eta_date'] || etaDate) && (
          <p>
            {t('ELIGIBILITY_VERIFICATION.ETA')}:{' '}
            {appointmentsDateTimeFormat(
              appointment?.['coverage_eta_date'] || etaDate,
              zone
            )}
          </p>
        )}
      </>
    );
  };
  const hasEtaDate = !!etaDate;

  /* Condition to show Verify Insurance link from the Appointment Object
      1. is_eligibility_verification_processing - false
      2. verification_status - not_verified
      3. verification_status - verified and It is not active insurance
      4. Logged in should have insurance role permission
  */
  const shouldShowVerifyInsuranceLink =
    appointment?.patient_id &&
    isEligibilityVerificationEnabled &&
    isUserInsuranceRolePermissionExists &&
    (appointment?.verification_status === VerificationStatusObj.NOT_VERIFIED ||
      (appointment?.verification_status === VerificationStatusObj.VERIFIED &&
        appointment?.is_manually_verified === false &&
        !isActiveInsurance(appointment?.eligibility_status))) &&
    appointment?.is_eligibility_verification_processing === false &&
    !isVerifying &&
    !isRefreshingInsurance &&
    !hasEtaDate;

  /* Condition to show Verification is processing from the Appointment Object
      1. is_eligibility_verification_processing - true
      2. Logged in should have insurance role permission
  */
  const isVerificationProcessing =
    isEligibilityVerificationEnabled &&
    isUserInsuranceRolePermissionExists &&
    (appointment?.is_eligibility_verification_processing === true ||
      hasEtaDate ||
      isVerifying) &&
    !isRefreshingInsurance;

  const shouldShowRefreshingInsuranceDetails = isRefreshingInsurance;

  /* Condition to show Refresh Insurance details from the Appointment Object
      1. is_eligibility_verification_processing - false
      2. verification_status - verified
      3. is_manually_verified - false
      4. coverages array should exist
      5. Logged in should have insurance role permission
  */
  const shouldShowRefreshInsuranceLink =
    isEligibilityVerificationEnabled &&
    isUserInsuranceRolePermissionExists &&
    hasCoverages &&
    appointment?.verification_status === VerificationStatusObj.VERIFIED &&
    isActiveInsurance(appointment?.eligibility_status) &&
    appointment?.is_eligibility_verification_processing === false &&
    appointment?.is_manually_verified === false &&
    !isVerifying &&
    !isRefreshingInsurance;

  return (
    <div>
      {/* Verify insurance link section */}
      {shouldShowVerifyInsuranceLink && renderVerifyInsuranceLink()}
      {/* Verification Processing label section */}
      {isVerificationProcessing && renderVerificationProcessing()}
      {/* Refresh Insurance Details link section */}
      {shouldShowRefreshInsuranceLink && renderRefreshInsuranceDetailsLink()}
      {/* Refreshing Insurance Details label section */}
      {shouldShowRefreshingInsuranceDetails &&
        renderRefreshingInsuranceDetails()}
      {/* Verify Insurance dialog section */}
      {verifyInsuranceModalState.isOpen && (
        <VerifyInsuranceModal
          isOpen={verifyInsuranceModalState.isOpen}
          handleClose={handleVerifyInsuranceModalClose}
          clinicGroupId={clinicGroupId}
          clinicId={clinicId}
          patientId={patientId}
          appointmentId={appointment?.id}
          defaultInsuranceValues={verifyInsuranceModalState.insurance}
          onCancel={() => {
            setIsVerifying(false);
            if (
              verifyingInsurance &&
              typeof verifyingInsurance === 'function'
            ) {
              verifyingInsurance(false);
            }
          }}
        />
      )}
    </div>
  );
};

export default VerifyInsurance;
